import { isMobileNpcOverlay } from "../game/viewport-breakpoint.js";

/** Minimum bottom reserve when mobile browser chrome is not reported via env/visualViewport. */
export const MOBILE_BROWSER_CHROME_MIN_PX = 64;

export type ViewportInsets = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

export type ViewportInsetsInput = {
  vw: number;
  innerHeight?: number;
  visualViewportHeight?: number;
  visualViewportOffsetTop?: number;
  safeAreaTop?: number;
  safeAreaBottom?: number;
  safeAreaLeft?: number;
  safeAreaRight?: number;
  /** Canvas already sized to visual viewport — only env(safe-area) padding applies. */
  boundsSynced?: boolean;
};

/** Gap between layout viewport bottom and visual viewport (dynamic browser toolbar). */
export function visualViewportBottomGap(input: {
  innerHeight: number;
  visualViewportHeight?: number;
  visualViewportOffsetTop?: number;
}): number {
  const vvH = input.visualViewportHeight;
  if (vvH == null) return 0;
  const gap = input.innerHeight - vvH - (input.visualViewportOffsetTop ?? 0);
  return Math.max(0, Math.round(gap));
}

/** Bottom/top/left/right insets for dialog shell layout (mobile-aware). */
export function computeViewportInsets(input: ViewportInsetsInput): ViewportInsets {
  const vvTop = Math.max(0, Math.round(input.visualViewportOffsetTop ?? 0));
  const top = input.boundsSynced
    ? Math.max(0, input.safeAreaTop ?? 0)
    : Math.max(0, input.safeAreaTop ?? 0, vvTop);
  const left = Math.max(0, input.safeAreaLeft ?? 0);
  const right = Math.max(0, input.safeAreaRight ?? 0);
  const envBottom = Math.max(0, input.safeAreaBottom ?? 0);

  if (input.boundsSynced) {
    return { top, left, right, bottom: envBottom };
  }

  const vvBottom =
    input.innerHeight != null
      ? visualViewportBottomGap({
          innerHeight: input.innerHeight,
          visualViewportHeight: input.visualViewportHeight,
          visualViewportOffsetTop: input.visualViewportOffsetTop,
        })
      : 0;
  const mobileChrome = isMobileNpcOverlay(input.vw) ? MOBILE_BROWSER_CHROME_MIN_PX : 0;
  return {
    top,
    left,
    right,
    bottom: Math.max(envBottom, vvBottom, mobileChrome),
  };
}

let safeAreaProbe: HTMLDivElement | null = null;

/** Read env(safe-area-inset-*) from a hidden probe (requires viewport-fit=cover). */
export function readEnvSafeAreaInsets(doc: Document = document): Pick<
  ViewportInsets,
  "top" | "bottom" | "left" | "right"
> {
  if (typeof document === "undefined") {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }
  if (!safeAreaProbe) {
    safeAreaProbe = doc.createElement("div");
    safeAreaProbe.setAttribute("aria-hidden", "true");
    safeAreaProbe.style.cssText =
      "position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);";
    doc.body.appendChild(safeAreaProbe);
  }
  const s = doc.defaultView?.getComputedStyle(safeAreaProbe);
  if (!s) return { top: 0, bottom: 0, left: 0, right: 0 };
  return {
    top: parseFloat(s.paddingTop) || 0,
    right: parseFloat(s.paddingRight) || 0,
    bottom: parseFloat(s.paddingBottom) || 0,
    left: parseFloat(s.paddingLeft) || 0,
  };
}

/** Live insets from window + env probe (call from layout paths in the browser). */
export function readLiveViewportInsets(
  vw: number,
  win: Window = window,
  options?: { boundsSynced?: boolean }
): ViewportInsets {
  const env = readEnvSafeAreaInsets(win.document);
  const vv = win.visualViewport;
  return computeViewportInsets({
    vw,
    innerHeight: win.innerHeight,
    visualViewportHeight: vv?.height,
    visualViewportOffsetTop: vv?.offsetTop,
    safeAreaTop: env.top,
    safeAreaBottom: env.bottom,
    safeAreaLeft: env.left,
    safeAreaRight: env.right,
    boundsSynced: options?.boundsSynced,
  });
}
